/* eslint-disable no-unused-vars,no-unused-expressions */
/* global G, PLATFORM */
// noinspection ExceptionCaughtLocallyJS

import { asyncPipeSpread, asyncPipeSpreadIf, bulk, getFirstItem, setKey } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import map from 'lib/sequence/model/api/map'
import { get, set } from 'lib/sequence/component/state/value'
import { hidden, hide, show } from 'lib/sequence/component/state/hidden'
import { empty } from 'lib/util/object'
import { displayFavoriteButtonsFromAttribute } from 'app/_shared/action/partial/favorite/display'
import settings from '@tenant/settings'
import asObject from 'lib/sequence/component/children/asObject'
import listInstallationReports from 'app/_shared/events/list'
import seqRead from 'lib/sequence/model/api/read'
import getQuickAction from 'app/_shared/events/appbar/quickActions/get'
import { logTypeAccess } from 'app/_shared/action/partial/logAccess'

const display = x => (get(x) ? show(x) : hide(x))
const bulkDisplay = bulk(display)
const bulkHide = bulk(hide)
const bulkShow = bulk(show)

const _setPropertyField = (field) => {
  const fieldValue = get(field)
  setKey(fieldValue.name, 'label', field[G.STATE])
  set(field, fieldValue.value)
}

/**
 * Returns whether certain parts of the ui should be hidden or obfuscated.
 *
 * @param {Gaia.AppModule.Spec} module  current module composition
 * @param {boolean} obfuscate           whether the EQ at hand should be obfuscated
 * @returns {boolean}
 */
const shouldObfuscate = (module, obfuscate) => obfuscate && obfuscate === module[G.MODEL][G.STATE][G.REF]

/**
 * Checks whether an installation report has been fetched for the current equipment
 *
 * Regardless of the installation report, it'll return `false` if
 * {@link shouldObfuscate} returns `true`.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(): *}
 */
const hasInstallationReport = module => (...args) => {
  const moduleState = module[G.STATE]
  const [_, { detail: { obfuscateSerial = moduleState.obfuscateSerial } = {} } = {}] = args || []
  const hasReport = module[G.MODEL][G.CHILDREN].installationReport?.[G.STATE]?.[G.REF]

  return hasReport && !shouldObfuscate(module, obfuscateSerial)
}

/**
 * Maps various properties inside the property box.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapProperties = module => async (components, ...args) => {
  const model = module[G.MODEL][G.CHILDREN]?.equipment || {}
  const { properties } = model[G.CHILDREN]
  const { title, detailsAsLabels, detailsAsProperties, additionalProperties } = components

  const bulkMapModel = bulk(map(model))
  bulkMapModel(title, additionalProperties)

  if (PLATFORM === 'web') {
    const {
      energy: energyLabel,
      voltage: voltageLabel,
      size: sizeLabel,
      type: typeLabel,
    } = asObject(detailsAsLabels[G.CHILDREN])

    map(properties)(detailsAsLabels)

    get(energyLabel) && _setPropertyField(energyLabel)
    get(voltageLabel) && _setPropertyField(voltageLabel)
    get(sizeLabel) && _setPropertyField(sizeLabel)
    get(typeLabel) && _setPropertyField(typeLabel)

    bulkDisplay(energyLabel, voltageLabel, sizeLabel, typeLabel)

    hidden(energyLabel)
    && hidden(voltageLabel)
    && hidden(sizeLabel)
    && hidden(typeLabel)
    && hide(detailsAsLabels)
  }

  if (PLATFORM === 'mobile') {
    const {
      energy: energyProperty,
      voltage: voltageProperty,
      size: sizeProperty,
    } = asObject(detailsAsProperties[G.CHILDREN])

    map(properties)(detailsAsProperties)

    get(energyProperty) && _setPropertyField(energyProperty)
    get(voltageProperty) && _setPropertyField(voltageProperty)
    get(sizeProperty) && _setPropertyField(sizeProperty)

    bulkDisplay(energyProperty, voltageProperty, sizeProperty)

    hidden(energyProperty)
    && hidden(voltageProperty)
    && hidden(sizeProperty)
    && hide(detailsAsProperties)
  }

  return [components, ...args]
}

/**
 * Sets the image of the equipment.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setImage = module => async (components, ...args) => {
  const model = module[G.MODEL][G.CHILDREN]?.equipment || {}
  const imageCache = model?.[G.CACHE]?.image || {}
  const { image, mobileImage } = components

  const equipmentImage = getFirstItem(imageCache)
  const targetComponent = PLATFORM === 'mobile'
    ? mobileImage
    : image

  !empty(equipmentImage)
  && (targetComponent[G.PROPS].attachment = {
    key: equipmentImage?.value?.attachmentId,
    value: equipmentImage?.attachment?.value,
  })

  return [components, ...args]
}

/**
 * Sets the icon of the equipment.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setIcon = module => async (components, ...args) => {
  const model = module[G.MODEL][G.CHILDREN]?.equipment || {}
  const productNumber = model[G.CACHE]?.number || null

  const { icon } = components

  const { documentationTreeFilter = null, documentationTreeNodeIcons = null } = settings

  if (documentationTreeFilter && documentationTreeNodeIcons) {
    const { product: products } = documentationTreeFilter
    const productIcons = documentationTreeNodeIcons.filter(nodeIcon => nodeIcon.nodeType === 'product')
    const targetProduct = products.find(product => product.number === productNumber)
    const targetProductNode = productIcons.find(productIcon => productIcon.name === targetProduct?.name || null)

    const { icon: targetIcon = null } = targetProductNode || {}
    setKey(targetIcon?.name, 'name', icon[G.STATE])
  }

  return [components, ...args]
}

/**
 * Sets the URL for the chat icon
 *
 * @param module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setChatUrl = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { equipment } = model[G.CHILDREN]
  const { serial = null } = equipment?.[G.CACHE] || {}

  const moduleState = module[G.STATE]
  const actionState = moduleState[G.ACTION][G.STATE]

  const { btnChat } = components
  const chatQuickAction = getQuickAction('chat', module, null, null)

  const { [G.HTTP]: httpAdapter } = model[G.ADAPTER]
  try {
    // We don't want the error middleware to kick in here. The server may return 400
    // if the user doesn't have the role to access the chat. By omitting the error
    // middleware, we will catch anything that's not 200 and hide the button without
    // showing an error modal.
    const options = {
      middleware: ({
        loader,
        persistence,
        language,
        impersonation,
      }) => [loader, persistence, language, impersonation],
    }

    // On mobile, we don't have the impersonation middleware, passing `options` to the http adapter
    // will cause an error, we need to exclude it
    const mobileOptions = {
      middleware: ({ loader, language }) => [loader, language],
    }

    const result = await httpAdapter.post(
      { url: '/api/v1/chatpdf/jumpurl', params: { serial } },
      PLATFORM === 'web' ? options : mobileOptions,
    )

    if (result?.externalUrl) {
      actionState.chatUrl = result.externalUrl
    } else {
      throw new ReferenceError(`External url is not defined: ${result}`)
    }
  } catch (e) {
    hide(btnChat)
    chatQuickAction && (chatQuickAction.hidden = true)
    console.error(e)
  }

  return [components, ...args]
}

/**
 * Determines whether the `serialLong` or `serial` field should be displayed.
 *
 * If `obfuscateSerial`, coming from the args, is `true`. It will hide the last
 * 5 digits of the serial number.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displaySerial = module => async (components, ...args) => {
  const moduleState = module[G.STATE]
  const { serial, serialLong } = components
  const [{ detail: { obfuscateSerial = moduleState.obfuscateSerial } = {} } = {}] = args || []
  const serialLongValue = get(serialLong)

  obfuscateSerial && setKey(obfuscateSerial, 'obfuscateSerial', moduleState)

  serialLongValue
    ? show(serialLong) && hide(serial)
    : get(serial) && hide(serialLong) && show(serial)

  shouldObfuscate(module, obfuscateSerial) && set(
    serialLongValue?.length ? serialLong : serial,
    serialLongValue?.length
      ? `${get(serialLong).slice(0, -7)}XXXXXXX`
      : 'XXXXX',
  )

  return [components, ...args]
}

/**
 * Fetches the installation report for the current equipment and maps the properties
 *
 * Will show/hide the installation report section based on availability of
 * installation report and the outcome of {@link shouldObfuscate}.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayInstallationReport = module => async (components, ...args) => {
  const moduleState = module[G.STATE]
  const [{ detail: { obfuscateSerial = moduleState.obfuscateSerial } = {} } = {}] = args || []

  const { installationReport } = module[G.MODEL][G.CHILDREN]
  const { installationReport: installationReportComponent, installedAtLabel, installationReportCustomerContainer } = components

  const result = installationReport[G.CACHE] === null
    ? await listInstallationReports(null, module, installationReportComponent, null)
    : null

  installationReport[G.CACHE] = result?.length
    ? getFirstItem(result)
    : installationReport[G.CACHE]

  installationReport[G.CACHE] && !shouldObfuscate(module, obfuscateSerial)
    ? bulkShow(installationReportComponent, installationReportCustomerContainer)
      && await seqRead(installationReport)(installationReportComponent)
    : bulkHide(installationReportComponent, installationReportCustomerContainer)
      && (installedAtLabel && hide(installedAtLabel))

  const { key: installationReportKey = null } = installationReport?.[G.CACHE] || {}

  // Sets G.REF for installationReport
  installationReportKey
    && !installationReport?.[G.STATE]?.[G.REF]
    && setKey(installationReportKey, G.REF, installationReport[G.STATE])

  // See GAIA-1382
  PLATFORM === 'web' && hide(installationReportComponent)
  PLATFORM === 'mobile' && hide(installationReportCustomerContainer)

  return [components, ...args]
}

/**
 * Maps properties of the customer column of the installation report
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayInstallationReportCustomer = module => async (components, ...args) => {
  const { installationReport } = module[G.MODEL][G.CHILDREN]
  const { installationReport: installationReportComponent, installationReportCustomer } = components

  const { [G.INTL]: intl } = module[G.ADAPTER]

  const { address: addressModel, name: nameModel } = installationReport[G.CHILDREN]
  const { city, street, country, zip, number } = addressModel[G.CACHE]

  const address = intl.address({ city, street, country, zip, number }, { inline: true })

  const {
    mobileCustomerName,
    mobileCustomerAddress,
  } = find(installationReportComponent)

  const unknownLabel = await intl._t(
    'label.unknown',
    {
      ns: 'common',
      _key: 'label.unknown',
      defaultValue: 'Unknown',
    },
  )

  if (PLATFORM === 'web') {
    setKey(nameModel[G.CACHE] || unknownLabel, 'label', installationReportCustomer[G.STATE])
    set(installationReportCustomer, address)
  } else if (PLATFORM === 'mobile') {
    set(mobileCustomerAddress, address)
    set(mobileCustomerName, nameModel[G.CACHE] || unknownLabel)
  }

  return [components, ...args]
}

/**
 * Maps properties of the installation column of the installation report
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayInstallationReportInstallation = module => async (components, ...args) => {
  const { installationReport } = module[G.MODEL][G.CHILDREN]
  const { submitter, installerIs, installerName, installationDate, installationDateUnknown } = installationReport[G.CHILDREN]
  const { installationReport: installationReportComponent } = components

  const {
    installationValue,
    mobileInstallationValue,
    mobileInstallationDate,
  } = find(installationReportComponent)

  const { [G.INTL]: intl } = module[G.ADAPTER]

  const installerLabel = intl.translate('label.installer', {
    ns: 'equipmentInformation',
    _key: 'label.installer',
    defaultValue: 'Installer',
  })

  const dateLabel = intl.translate('label.date', {
    ns: 'common',
    _key: 'label.date',
    defaultValue: 'Date',
  })

  const unknownLabel = intl.translate('personalOptions.unknown', {
    ns: 'generic',
    _key: 'personalOptions.unknown',
    defaultValue: 'Unknown',
  })

  const { value: { name: submitterName = unknownLabel } = {} } = getFirstItem(submitter[G.CACHE]) || {}
  const installerIsValue = installerIs[G.CACHE]
  const installerNameValue = installerName[G.CACHE]
  const installationDateValue = installationDate[G.CACHE]
  const installationDateUnknownValue = installationDateUnknown[G.CACHE]

  const targetInstallerName = (installerIsValue === 'me' && submitterName)
      || (installerIsValue === 'another' && installerNameValue)
      || (installerIsValue === 'unknown' && unknownLabel)

  const targetInstallationDate = (installationDateUnknownValue && `${dateLabel} ${unknownLabel}`)
      || intl.getLocaleDate(installationDateValue)

  if (PLATFORM === 'web') {
    set(installationValue, `${installerLabel} ${targetInstallerName}`)
    setKey(targetInstallationDate, 'label', installationValue[G.STATE])
  } else if (PLATFORM === 'mobile') {
    set(mobileInstallationValue, targetInstallerName)
    set(mobileInstallationDate, targetInstallationDate)
  }

  return [components, ...args]
}

/**
 * Maps properties of the note column of the installation report
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayInstallationReportNote = module => async (components, ...args) => {
  const { installationReport } = module[G.MODEL][G.CHILDREN]
  const { note } = installationReport[G.CHILDREN]
  const { installationReport: installationReportComponent } = components

  const { installationNote, mobileInstallationNote } = find(installationReportComponent)

  if (PLATFORM === 'web') {
    set(installationNote, note[G.CACHE])
  } else if (PLATFORM === 'mobile') {
    set(mobileInstallationNote, note[G.CACHE])
  }

  return [components, ...args]
}

/**
 * Displays the correct create/edit button based on the installation report
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayInstallationReportButton = module => async (components, ...args) => {
  const { installationReport: installationReportComponent } = components

  const { btnEditRegister, btnRegister } = components

  hide(btnRegister)
  show(btnEditRegister)

  return [components, ...args]
}

/**
 * Sets the equipment status regarding the installation report
 *
 * Will show/hide the installation report status based on availability of
 * installation report and the outcome of {@link shouldObfuscate}.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setInstallationReportStatus = module => async (components, ...args) => {
  const moduleState = module[G.STATE]
  const [{ detail: { obfuscateSerial = moduleState.obfuscateSerial } = {} } = {}] = args || []
  const { installationReport } = module[G.MODEL][G.CHILDREN]
  const installationReportStatus = installationReport[G.CACHE]
    ? 'registered'
    : 'unregistered'

  const { installationReportStatus: installationReportStatusComponent } = components

  set(installationReportStatusComponent, installationReportStatus)

  shouldObfuscate(module, obfuscateSerial) && hide(installationReportStatusComponent)

  return args
}

/**
 * Shows the correction action depending on the absence/presence of an installation report
 *
 * Will show/hide the installation report actions based on availability of
 * installation report and the outcome of {@link shouldObfuscate}.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayInstallationReportAction = module => async (components, ...args) => {
  const moduleState = module[G.STATE]
  const [{ detail: { obfuscateSerial = moduleState.obfuscateSerial } = {} } = {}] = args || []
  const actions = module[G.STATE][G.ACTION][G.COMPONENT][G.ACTIONS]
  const { btnRegister } = components

  shouldObfuscate(module, obfuscateSerial) || hasInstallationReport(module)(args)
    ? bulkHide(actions[0], btnRegister)
    : bulkShow(actions[0], btnRegister)

  return [components, ...args]
}

/**
 * EquipmentInformation Equipment Action
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*): function(...[*]): Promise<*[]>}
 */
export default module => () => async (...args) => asyncPipeSpread(
  displayFavoriteButtonsFromAttribute('equipment')(module),
  mapProperties(module),
  setImage(module),
  setIcon(module),
  setChatUrl(module),
  displaySerial(module),
  displayInstallationReport(module),
  asyncPipeSpreadIf(hasInstallationReport(module))(
    displayInstallationReportCustomer(module),
    displayInstallationReportInstallation(module),
    displayInstallationReportNote(module),
    displayInstallationReportButton(module),
  ),
  displayInstallationReportAction(module),
  logTypeAccess('equipment')(module),
  setInstallationReportStatus(module),
)(find(module[G.STATE][G.ACTION][G.COMPONENT]), ...args)
